/**
 * Seed - Composable seed from spritesheet
 *
 * Layers 2 sprites:
 * - Base seed
 * - Glowing seed (overlays when active)
 */

import { Container, Sprite, Texture, Assets, Rectangle } from 'pixi.js';

export class Seed extends Container {
  private base: Sprite;
  private glow: Sprite;

  private frameHeight = 0;

  constructor() {
    super();

    // Sprites will be created after loading
    this.base = new Sprite();
    this.glow = new Sprite();

    // Add in layering order - glow on top
    this.addChild(this.base);
    this.addChild(this.glow);

    // Initially hide glow
    this.glow.alpha = 0;
  }

  /**
   * Animate seed floating down slowly with back-and-forth wobble
   */
  async floatDown(targetY: number, duration: number = 4000): Promise<void> {
    const startY = -this.height; // Start above screen
    const startX = this.x; // Remember starting X position
    this.y = startY;

    const startTime = Date.now();

    return new Promise((resolve) => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out for gentle landing
        const eased = 1 - Math.pow(1 - progress, 3);

        // Vertical position
        this.y = startY + (targetY - startY) * eased;

        // Side-to-side wobble (like pendulum)
        const wobbleFrequency = 1.5; // 1.5 full swings during descent
        const wobbleAmount = 60; // ±60 pixels
        const dampening = 1 - progress * 0.3; // Reduce wobble as it lands
        const wobble = Math.sin(progress * Math.PI * 2 * wobbleFrequency) * wobbleAmount * dampening;

        this.x = startX + wobble;

        // Gentle rotation matching wobble
        const rotationAmount = Math.sin(progress * Math.PI * 2 * wobbleFrequency) * 0.15 * dampening;
        this.rotation = rotationAmount;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.rotation = 0; // Reset rotation at end
          this.x = startX; // Return to center position
          resolve();
        }
      };

      animate();
    });
  }

  /**
   * Fade out the seed
   */
  public fadeOut(duration: number): Promise<void> {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const startAlpha = 1;

      const animate = () => {
        const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

        // Fade from 1 → 0
        this.alpha = startAlpha * (1 - progress);
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      animate();
    });
  }

  /**
   * Load and setup spritesheet
   */
  async load(): Promise<void> {
    // Load the spritesheet (2 frames horizontal: seed + glow)
    const texture = await Assets.load('/assets/introduction/seed.png');

    // Calculate frame dimensions (2 frames horizontal)
    this.frameHeight = texture.height;

    // Extract frames from spritesheet
    const frames = this.extractFrames(texture, 2);

    // Assign textures to sprites
    this.base.texture = frames[0]!;
    this.glow.texture = frames[1]!;
    

    // Center both sprites at exact same position
    this.base.anchor.set(0.5);
    this.base.x = 0;
    this.base.y = 0;

    this.glow.anchor.set(0.5);
    this.glow.x = 62;
    this.glow.y = 0;

    // High quality rendering
    this.base.texture.source.scaleMode = 'linear';
    this.glow.texture.source.scaleMode = 'linear';


    console.log('✓ Seed loaded (base + glow layered)');
  }

  /**
   * Extract frames from horizontal spritesheet
   */
  private extractFrames(texture: Texture, count: number): Texture[] {
    const frames: Texture[] = [];
    const frameWidth = texture.width / count;
    const frameHeight = texture.height;

    for (let i = 0; i < count; i++) {
      const frame = new Texture({
        source: texture.source,
        frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight)
      });
      frames.push(frame);
    }

    return frames;
  }

  /**
   * Show subtle glow
   */
  showGlow(alpha: number = 1): void {
    this.glow.alpha = alpha;
  }


  /**
   * Hide glow
   */
  hideGlow(): void {
    this.glow.alpha = 0;
  }

  /**
   * Pulse glow effect (fades between no glow and full glow)
   * Returns ticker function
   */
  startGlowPulse(): () => void {
    const pulseSpeed = 0.0008; // Very slow pulse
    const startTime = Date.now();

    const animate = () => {
      if (!this.glow) return;

      const elapsed = Date.now() - startTime;
      const cycle = Math.sin(elapsed * pulseSpeed);
      // Pulse between 0 (invisible/no glow) and 1 (full glow)
      this.glow.alpha = (cycle + 1) / 2;
    };

    return animate;
  }

  /**
   * Wiggle animation (when tapped)
   */
  async wiggle(): Promise<void> {
    const duration = 300;
    const startTime = Date.now();
    const startRotation = this.rotation;

    return new Promise((resolve) => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Elastic wiggle
        const wiggleAmount = Math.sin(progress * Math.PI * 4) * (1 - progress) * 0.15;
        this.rotation = startRotation + wiggleAmount;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.rotation = startRotation;
          resolve();
        }
      };

      animate();
    });
  }

  /**
   * Set size (maintains aspect ratio)
   */
  setSize(size: number): void {
    this.scale.set(size / this.frameHeight);
  }
}
